/**
 * Supplier Dealer Network directory visibility for dealer branches.
 *
 * Deactivated (hidden) branches are not offered as places to start new work
 * in the Dealer Network directory. They remain available for in-flight work
 * (support tickets, email deep links) and for name/abbreviation resolution.
 *
 * Filtering is display-only at directory consumers (Tenant Network). Do not
 * filter at allFranchiseeDetails.branches, during license attribution
 * (must use the full DateCreated-ordered list), or in getPAT / bridge / ticket login.
 *
 * Hidden is a usability flag, not a security boundary.
 */
import { sameText } from '../../../webmodule-common/other/ui/string-helper-functions.js';

/** True when the branch must be omitted from directory listings (Login to Branch, etc.). */
export function isBranchHiddenFromDirectory(branch: { hidden?: boolean } | null | undefined): boolean {
  return branch?.hidden === true;
}

/** Minimal branch shape required for directory attribution / display selection. */
export interface DirectorySourceBranch {
  branchId: string;
  name: string;
  hidden?: boolean;
}

/** Tenant login row used only for per-branch Licenses Used attribution. */
export interface DirectoryTenantUser {
  active: boolean;
  claims: { [key: string]: string } | null;
}

export type DirectoryChildKind = 'visible-branch' | 'unavailable';

/**
 * One child row the Tenant Network table should render under a dealership group.
 */
export interface DirectoryChildSpec {
  kind: DirectoryChildKind;
  /** Real branch id, or the empty-guid placeholder for Unavailable. */
  branchId: string;
  /** Display name for filter isolation / visible rows; localized unavailable label. */
  name: string;
  /** Active users attributed to this branch (full-list rules, including hidden). */
  licensesUsed: number;
  /**
   * Index into the caller-supplied branch array for `visible-branch`.
   * -1 for the synthetic unavailable row.
   */
  sourceIndex: number;
}

/**
 * Attributes active users across the full DateCreated-ordered branch list, then
 * chooses which child rows the Dealer Network directory should show.
 *
 * Caller must supply branches already ordered by DateCreated (supplier cache:
 * order by deployment, tenant, branch.DateCreated). Index 0 is the main branch
 * for invalid or missing branch claims.
 *
 * - Empty input -> Unavailable placeholder on the working list (main = index 0).
 * - Hidden branches still receive license attribution so remaining rows stay accurate.
 * - Hidden branches are omitted from returned children (display filter only).
 * - Unavailable is never treated as hidden.
 * - All real branches hidden -> empty list (caller hides the dealership).
 *
 * @param unavailableLabel Localized label for the no-sync placeholder (view passes tlang).
 */
export function buildDirectoryChildSpecs(
  branches: readonly DirectorySourceBranch[],
  tenantUsers: readonly DirectoryTenantUser[],
  branchClaimKey: string,
  emptyBranchId: string,
  unavailableLabel = 'Unavailable'
): DirectoryChildSpec[] {
  // No synced branches yet: insert Unavailable so attribution still runs
  // (invalid claims home to main = this placeholder).
  const workingList: DirectorySourceBranch[] =
    branches.length === 0 ? [{ branchId: emptyBranchId, name: unavailableLabel, hidden: false }] : [...branches];

  const isInvalidBranch = (id: string): boolean => workingList.find(c => sameText(c.branchId, id)) === undefined;

  const children: DirectoryChildSpec[] = [];

  workingList.forEach((branch, index) => {
    const isMainBranch = index === 0;

    // Attribute users whose claim matches this branch, or invalid claims on main.
    const branchUsers = tenantUsers.filter(user => {
      const claimBranchId = user.claims?.[branchClaimKey] ?? '';
      return sameText(claimBranchId, branch.branchId) || (isInvalidBranch(claimBranchId) && isMainBranch);
    });
    const licensesUsed = branchUsers.filter(user => user.active).length;

    const isUnavailablePlaceholder = branch.branchId === emptyBranchId;
    // Display filter: omit deactivated branches; keep Unavailable.
    if (isUnavailablePlaceholder || !isBranchHiddenFromDirectory(branch)) {
      children.push({
        kind: isUnavailablePlaceholder ? 'unavailable' : 'visible-branch',
        branchId: branch.branchId,
        name: branch.name,
        licensesUsed,
        // Synthetic Unavailable has no source row in the caller's array.
        sourceIndex: branches.length === 0 ? -1 : index
      });
    }
  });

  return children;
}
